import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { TagEditorComponent, type TagEditorItem } from '@tourfold/libraries/shared-ui';
import type {
  DocumentDetailResponse,
  DocumentExtractionResponse,
  DocumentResponse,
  DocumentVersionResponse,
  FolderDetailResponse,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { DocumentsService, uploadBinary } from '../services/documents.service';
import { TagsService } from '../services/tags.service';
import type { FilePreviewDialogData } from '@tourfold/libraries/file-preview';
import {
  DOCUMENT_EXTRACTION_EVENTS,
  type DocumentExtractionEvent,
  isRunningExtractionStatus,
} from '../services/document-extraction-events.service';
import { DocumentManagementPreferencesService } from '../services/document-management-preferences.service';
import { DOCUMENT_MANAGEMENT_CAPABILITIES } from '../services/document-management-capabilities';
import { parseProblemFromError } from '../services/extract-problem-message';
import { FoldersService } from '../services/folders.service';
import { translateProblem } from '../services/translate-problem';
import {
  folderOrDocumentNameErrorKey,
  validateFolderOrDocumentName,
} from '../services/folder-or-document-name';
import { mimeColorClass } from './mime-color';
import type { StarTarget } from '../folder-browser/folder-browser.component';

export type DetailSelection =
  | { readonly kind: 'folder'; readonly id: string }
  | { readonly kind: 'document'; readonly id: string };

/**
 * Payload broadcast after an inline rename succeeds. `parentId` is the immediate parent of the
 * renamed entity — `folderId` for documents, `parentId` for folders — so the receiving browser
 * can pick the right column to refetch (`null` is the tenant root).
 */
export interface RenameNotice {
  readonly kind: 'folder' | 'document';
  readonly id: string;
  readonly parentId: string | null;
  readonly name: string;
}

/**
 * Right-hand pane of the folder browser. Shows metadata about whatever the user has selected:
 * a folder (created/kind) or a document (mime, size, action buttons). Empty state prompts the
 * user to pick something or drag files to upload.
 *
 * Network: this component owns its own fetch for the selected entity so the parent browser can
 * pass just an `{ kind, id }` reference and not worry about caching detail responses.
 */
@Component({
  selector: 'tf-detail-pane',
  imports: [
    TranslateModule,
    ButtonModule,
    ConfirmDialogModule,
    DialogModule,
    InputTextModule,
    ProgressSpinnerModule,
    TooltipModule,
    TagEditorComponent,
    DatePipe,
  ],
  providers: [DialogService, ConfirmationService],
  templateUrl: './detail-pane.component.html',
  styleUrl: './detail-pane.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class DetailPaneComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly documentsService = inject(DocumentsService);
  private readonly extractionEvents = inject(DOCUMENT_EXTRACTION_EVENTS);
  private readonly documentManagementPreferences = inject(DocumentManagementPreferencesService);
  private readonly foldersService = inject(FoldersService);
  private readonly translate = inject(TranslateService);
  private readonly dialogService = inject(DialogService);
  private readonly tagsService = inject(TagsService);
  private readonly capabilities = inject(DOCUMENT_MANAGEMENT_CAPABILITIES);
  private readonly confirmationService = inject(ConfirmationService);

  protected readonly availableTags = computed<TagEditorItem[]>(() =>
    this.tagsService.availableTags().flatMap((t) =>
      // The global TagsApi response types id/name/color as optional (codegen quirk); the backend
      // always populates them. Drop rows that come back partial rather than flooding the editor
      // with `undefined`-titled chips.
      t.id && t.name && t.color ? [{ id: t.id, name: t.name, color: t.color }] : []
    )
  );

  /**
   * Whether the current selection supports tag editing. Four gates ANDed together:
   *  1. The user has the `documents.update` resource action (host-provided via the capabilities
   *     token — defaults to true outside the web-app shell).
   *  2. The selection is not soft-deleted — soft-deleted rows are read-only across the pane.
   *  3. Folders allow it only when the kind is `user` (matches the backend's
   *     `cannot_modify_managed_folder` rule on `tag_ids`).
   *  4. Documents allow it for finalized rows (has `current_version_id`); the backend rejects
   *     incomplete documents on PATCH.
   */
  protected readonly canEditTags = computed(() => {
    if (!this.capabilities.canUpdateDocuments()) return false;
    if (this.isSoftDeleted()) return false;
    const sel = this.selection();
    if (!sel) return false;
    if (sel.kind === 'folder') return this.folder()?.kind === 'user';
    return this.document()?.currentVersionId != null;
  });

  /**
   * Whether the autocomplete dropdown should offer the "Create '<query>'" inline-create suggestion.
   * Maps directly to the host-provided `tags.create` capability — independent of {@link canEditTags}
   * because we want to express "can attach existing tags but not define new ones".
   */
  protected readonly canCreateTags = computed(() => this.capabilities.canCreateTags());

  /** True when the currently-selected row is in the user's Trash (soft-deleted). */
  protected readonly isSoftDeleted = computed(() => {
    const sel = this.selection();
    if (!sel) return false;
    // Folders are never soft-deleted in the new model; only documents carry a soft-delete state.
    return sel.kind === 'document' && this.document()?.softDeletedAt != null;
  });

  /**
   * Delete button visibility (documents only). Hidden for already-soft-deleted rows and gated on the
   * `documents.delete` capability — the backend gates soft-delete on the same grant as hard-delete.
   */
  protected readonly canSoftDelete = computed(() => {
    if (!this.capabilities.canDeleteDocuments()) return false;
    if (this.isSoftDeleted()) return false;
    const sel = this.selection();
    if (sel?.kind !== 'document') return false;
    return this.document()?.currentVersionId != null;
  });

  /**
   * Delete button visibility (folders only). Folders are never soft-deleted in the new model — the
   * "Delete" button cascades a hard-delete: the folder subtree disappears and every contained
   * document is soft-deleted (with `original_path` captured) so it can be restored individually.
   * Gated on the `documents.delete` capability and only available for `user` folders (managed
   * folders are pinned by the owning feature).
   */
  protected readonly canDeleteFolder = computed(() => {
    if (!this.capabilities.canDeleteDocuments()) return false;
    const sel = this.selection();
    if (sel?.kind !== 'folder') return false;
    return this.folder()?.kind === 'user';
  });

  /**
   * Restore button visibility (documents only). Only shown when the document is soft-deleted and the
   * user has the `documents.update` capability. Folders are never soft-deleted → never restored.
   */
  protected readonly canRestore = computed(
    () => this.isSoftDeleted() && this.capabilities.canUpdateDocuments()
  );

  /**
   * Permanent-delete button visibility (documents only). The backend rejects hard-delete on a
   * live row (`document-management/must-be-soft-deleted-first`), so the affordance only shows
   * after the document has been moved to Deleted. Same `documents.delete` capability gate as
   * the soft-delete button.
   */
  protected readonly canHardDelete = computed(
    () => this.isSoftDeleted() && this.capabilities.canDeleteDocuments()
  );

  /**
   * Affordances that mutate the selected row are disabled across the board for soft-deleted
   * selections — the user must restore first. The backend mirrors each of these (star: rejects
   * with `cannot-modify-soft-deleted-document`; description / version upload / extractions: PATCH/upload
   * paths reject soft-deleted rows). Hiding the controls keeps the pane visually consistent: soft-deleted
   * rows appear read-only without surface-level dead buttons.
   */
  protected readonly canStar = computed(() => !this.isSoftDeleted());
  protected readonly canEditDescription = computed(() => !this.isSoftDeleted());
  protected readonly canAnalyze = computed(
    () => !this.isSoftDeleted() && this.document()?.currentVersionId != null
  );
  protected readonly canUploadNewVersion = computed(() => !this.isSoftDeleted());

  /** Disables the soft-delete/restore buttons while a request is in flight, avoiding double-submits. */
  protected readonly lifecycleSaving = signal(false);

  /**
   * Inline error surfaced under the title when soft-delete or restore fails (network, permission,
   * unhandled problem types). The specific `document-management/name-taken` case on restore is
   * intercepted earlier and opens the rename dialog instead — see {@link onRestore}.
   */
  protected readonly lifecycleError = signal<string | null>(null);

  // ── Rename-on-restore dialog state ───────────────────────────────────
  // Opens automatically when restore returns NAME_TAKEN. Pre-fills with the original name so the
  // user can edit (typically appending " (restored)" or similar). Save calls restore with the new
  // name as a query param; the backend renames + restores in one transaction. Errors stay
  // inside the dialog (its own `restoreRenameError` signal) so the inline `lifecycleError` banner
  // doesn't double up while the user is interacting with the prompt.
  protected readonly restoreRenameDialogVisible = signal(false);
  protected readonly restoreRenameDraft = signal('');
  protected readonly restoreRenameError = signal<string | null>(null);

  readonly selection = input<DetailSelection | null>(null);

  /**
   * True when the pane should render the "select a folder or document" empty state instead of
   * folder details. Three triggers:
   *  - nothing selected,
   *  - a managed root folder is selected (kind `managed` — e.g. the synthetic root of a scoped
   *    browser like the custom-object docs tab),
   *  - a managed branch folder is selected (kind `managed_branch`).
   * Managed and managed-branch folders have no editable surface (no rename, no delete, no tags),
   * so showing their metadata adds noise without giving the user anything to act on. Returns
   * `false` while a folder selection is still resolving so the loading branch wins until the
   * `kind` is known.
   */
  protected readonly showEmptyState = computed(() => {
    const sel = this.selection();
    if (!sel) return true;
    if (sel.kind !== 'folder') return false;
    const folder = this.folder();
    if (!folder) return false;
    return folder.kind === 'managed' || folder.kind === 'managed_branch';
  });
  /** Caller-provided counts so we can render "X documents · Y subfolders" without an extra request. */
  readonly folderChildCounts = input<{ folders: number; documents: number } | null>(null);
  /**
   * Per-user starred set, intersected by id when rendering the star toggle in the header. Owned
   * by the parent browser so a single source of truth covers both column rows and detail header.
   */
  readonly starredIds = input<ReadonlySet<string>>(new Set());
  /**
   * Translated error message from the parent for the most recent star/unstar failure. Owned by
   * the parent because that's where the API call is made; rendered here in the lifecycle-error
   * banner so the user sees feedback close to the star button. Clears on selection change.
   */
  readonly starError = input<string | null>(null);

  readonly closeRequested = output<void>();
  readonly starToggled = output<StarTarget>();
  /**
   * Emitted when the user clicks a row in the folder's "Recently added" list — the parent should
   * point the detail-pane selection at that document. The Miller-column trail is intentionally
   * unchanged because the document may live deeper than the columns currently show.
   */
  readonly recentDocumentClicked = output<DocumentResponse>();
  /**
   * Emitted after a successful inline rename. The browser uses this to refresh the column the
   * row lives in (so the visible label updates) and, for folders, to update the trail's column
   * header label if the renamed folder has children open in a column further to the right.
   */
  readonly renamed = output<RenameNotice>();
  /**
   * Emitted after a tag set change (add or remove) so the parent browser can refresh the column
   * row that displays this folder/document — chip strips on rows have to update too. `parentId`
   * is `folderId` for documents and `parentId` for folders, mirroring the rename payload shape.
   */
  readonly tagsUpdated = output<{
    readonly kind: 'folder' | 'document';
    readonly id: string;
    readonly parentId: string | null;
  }>();
  /**
   * Emitted after a successful soft-delete (document) or hard-delete (folder). `parentId` follows
   * the same rule as the rename and tags payloads (folder's `parentId`, document's `folderId`).
   * The browser uses it to refresh the column the row belongs to so the row drops out of the
   * live listing without a manual reload.
   */
  readonly softDeleted = output<{
    readonly kind: 'folder' | 'document';
    readonly id: string;
    readonly parentId: string | null;
  }>();
  /**
   * Emitted after a successful hard-delete on a soft-deleted document. Same payload shape as
   * {@link softDeleted}; the parent uses it to refresh the Trash smart column, drop the row
   * from the column it lived in, and clear selection (the row is gone — `?doc=` should also
   * disappear from the URL).
   */
  readonly hardDeleted = output<{
    readonly kind: 'folder' | 'document';
    readonly id: string;
    readonly parentId: string | null;
  }>();
  readonly restored = output<{
    readonly kind: 'folder' | 'document';
    readonly id: string;
    readonly parentId: string | null;
  }>();

  protected readonly folder = signal<FolderDetailResponse | null>(null);
  protected readonly document = signal<DocumentDetailResponse | null>(null);
  /**
   * Display-only path segments captured on the document at soft-delete time (closest-to-root first).
   * Sourced directly from `DocumentDetailResponse.originalPath` — the backend records the path
   * text when the document is soft-deleted (e.g. `/Tax/2024/Q1/`) so the UI can show "originally in"
   * even when the original folders no longer exist. Always empty for live or folder selections.
   */
  protected readonly originalPath = signal<readonly string[]>([]);
  protected readonly versions = signal<readonly DocumentVersionResponse[]>([]);
  /**
   * Up to 5 most recently uploaded documents in the selected folder's subtree (folder + descendants),
   * fetched alongside the folder detail. Surfaces "what's new in here?" without forcing the user to
   * drill in.
   */
  protected readonly folderRecentDocuments = signal<readonly DocumentResponse[]>([]);
  protected readonly versionDownloadingId = signal<string | null>(null);
  protected readonly isUploadingNewVersion = signal(false);
  protected readonly newVersionError = signal<string | null>(null);
  protected readonly isLoading = signal(false);
  protected readonly hasError = signal(false);
  protected readonly downloadInFlight = signal(false);

  // ── Description editor state ─────────────────────────────────────────
  // Inline edit on the document detail. PATCH succeeds → we replace the local snapshot with the
  // server's response (canonical truth). Cancel restores the textarea to the saved value.
  protected readonly descriptionEditing = signal(false);
  protected readonly descriptionDraft = signal('');
  protected readonly descriptionSaving = signal(false);
  protected readonly descriptionError = signal<string | null>(null);

  // ── Title (rename) editor state ──────────────────────────────────────
  // Inline rename of the selected folder or document. The save path is shared between both kinds;
  // the active selection determines which service the patch goes through.
  protected readonly titleEditing = signal(false);
  protected readonly titleDraft = signal('');
  protected readonly titleSaving = signal(false);
  protected readonly titleError = signal<string | null>(null);
  protected readonly titleInput = viewChild<ElementRef<HTMLInputElement>>('titleInput');

  /**
   * True when the current selection supports renaming. Three gates:
   *  - live: soft-deleted rows are read-only across the pane (restore first to rename).
   *  - folders: must be `user` kind. Managed and `managed_branch` are pinned by the owning feature
   *    and the backend rejects with `cannot-modify-managed-folder`.
   *  - documents: must be finalized (`current_version_id` set) — incomplete rows have no canonical
   *    state to rename.
   */
  protected readonly canRename = computed(() => {
    if (this.isSoftDeleted()) return false;
    const sel = this.selection();
    if (!sel) return false;
    if (sel.kind === 'folder') {
      return this.folder()?.kind === 'user';
    }
    return this.document()?.currentVersionId != null;
  });

  // ── AI extraction state ──────────────────────────────────────────────
  // SSE is the primary completion signal. A scoped poll loop remains as backup for reconnects,
  // missed events, and browser/network interruptions.
  protected readonly extraction = signal<DocumentExtractionResponse | null>(null);
  protected readonly extractionHistory = signal<readonly DocumentExtractionResponse[]>([]);
  protected readonly historyExpanded = signal(false);
  protected readonly extractionStarting = signal(false);
  protected readonly extractionError = signal<string | null>(null);

  /** Cancellation flag for the in-flight poll loop, flipped when the selection changes. */
  private extractionPollCancel: { cancelled: boolean } | null = null;
  private lastConnectionFallbackRefreshKey: string | null = null;

  protected readonly extractedFieldRows = computed(() =>
    fieldRowsFromExtraction(this.extraction())
  );
  protected readonly isAiAnalysisExpanded = this.documentManagementPreferences.aiAnalysisExpanded;
  protected readonly isExtractionRunning = computed(() =>
    isRunningExtractionStatus(this.extraction()?.status)
  );

  // ── Preview state ────────────────────────────────────────────────────
  // `previewUrl` is the presigned JPEG inline preview URL, populated when the document's current
  // version reports `preview_available: true`. The detail-pane renders the JPEG in a clickable
  // tile; clicking opens a fullscreen overlay that picks the renderer (PDF.js / image / markdown
  // / text) by MIME type using the original file.
  protected readonly previewUrl = signal<string | null>(null);
  protected readonly previewLoading = signal(false);
  /** True when the overlay can render this document inline (PDF/image/markdown/text). */
  protected readonly canOpenInOverlay = computed(() =>
    isOverlayPreviewable(this.document()?.mimeType)
  );

  /** Hidden file input — the "Upload new version" button programmatically triggers this. */
  protected readonly newVersionInput = viewChild<ElementRef<HTMLInputElement>>('newVersionInput');

  protected readonly currentMimeClass = computed(() => mimeColorClass(this.document()?.mimeType));

  protected readonly folderSubtitle = computed(() => {
    const counts = this.folderChildCounts();
    if (!counts) return '';
    const docs = this.translate.instant(
      counts.documents === 1
        ? 'DOCUMENT_MANAGEMENT.SUBTITLE_DOCUMENTS_ONE'
        : 'DOCUMENT_MANAGEMENT.SUBTITLE_DOCUMENTS_OTHER',
      { count: counts.documents }
    );
    const folders = this.translate.instant(
      counts.folders === 1
        ? 'DOCUMENT_MANAGEMENT.SUBTITLE_SUBFOLDERS_ONE'
        : 'DOCUMENT_MANAGEMENT.SUBTITLE_SUBFOLDERS_OTHER',
      { count: counts.folders }
    );
    return `${docs} · ${folders}`;
  });

  constructor() {
    // Tag definitions are tenant-wide and rarely change — load once and let the cache feed
    // every subsequent tf-tag-editor instance.
    this.tagsService.ensureLoaded();

    this.extractionEvents.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => void this.onExtractionEvent(event));

    effect(() => {
      const connectionState = this.extractionEvents.connectionState();
      const extraction = this.extraction();
      const sel = this.selection();
      if (
        connectionState === 'connected' ||
        sel?.kind !== 'document' ||
        !extraction?.id ||
        !extraction.documentId ||
        !extraction.versionId ||
        !isRunningExtractionStatus(extraction.status)
      ) {
        this.lastConnectionFallbackRefreshKey = null;
        return;
      }

      const key = `${connectionState}:${extraction.id}:${extraction.status}`;
      if (this.lastConnectionFallbackRefreshKey === key) return;
      this.lastConnectionFallbackRefreshKey = key;
      void this.refreshExtractionFromServer(
        extraction.id,
        extraction.documentId,
        extraction.versionId
      );
    });

    // Original path for soft-deleted documents. The backend captures the path text into
    // `original_path` at soft-delete time (e.g. `/Tax/2024/Q1/`); we just split it for display.
    // Folders are never soft-deleted in the new model — only documents carry an `originalPath`.
    effect(() => {
      const sel = this.selection();
      if (!sel || sel.kind !== 'document' || !this.isSoftDeleted()) {
        this.originalPath.set([]);
        return;
      }
      const pathText = this.document()?.originalPath ?? '';
      const segments = pathText.split('/').filter((s) => s.length > 0);
      this.originalPath.set(segments);
    });

    effect(() => {
      const sel = this.selection();

      // Always reset transient/edit state — none of it should follow the user across selections.
      this.newVersionError.set(null);
      this.hasError.set(false);
      this.descriptionEditing.set(false);
      this.descriptionDraft.set('');
      this.descriptionError.set(null);
      this.descriptionSaving.set(false);
      this.titleEditing.set(false);
      this.titleDraft.set('');
      this.titleError.set(null);
      this.titleSaving.set(false);
      this.lifecycleSaving.set(false);
      this.lifecycleError.set(null);
      this.restoreRenameDialogVisible.set(false);
      this.restoreRenameDraft.set('');
      this.restoreRenameError.set(null);
      this.originalPath.set([]);
      this.extractionStarting.set(false);
      this.extractionError.set(null);
      this.historyExpanded.set(false);
      if (this.extractionPollCancel) {
        this.extractionPollCancel.cancelled = true;
        this.extractionPollCancel = null;
      }

      if (!sel) {
        this.folder.set(null);
        this.document.set(null);
        this.versions.set([]);
        this.folderRecentDocuments.set([]);
        this.extraction.set(null);
        this.extractionHistory.set([]);
        this.previewUrl.set(null);
        this.previewLoading.set(false);
        this.isLoading.set(false);
        return;
      }

      // Preview state never carries across selections — the image/spinner must always belong to
      // the row just clicked, even doc → doc. `loadDocument` refetches it for docs that have one.
      this.previewUrl.set(null);
      this.previewLoading.set(false);

      // Cross-kind switches drop the opposite kind's state so the wrong template branch can't
      // render stale data. Same-kind switches keep the existing metadata visible until `load()`
      // returns, which eliminates flicker; the new data overwrites atomically in `load*`.
      //
      // The AI extraction is the exception: it belongs to a specific document, and `loadDocument`
      // only overwrites it when the newly selected document actually has an extraction. If we
      // kept it across a doc → doc switch, selecting a document without an analysis would leave
      // the previous document's AI description on screen (TF-1979). Always reset it here.
      this.extraction.set(null);
      this.extractionHistory.set([]);
      if (sel.kind === 'folder') {
        this.document.set(null);
        this.versions.set([]);
      } else {
        this.folder.set(null);
        this.folderRecentDocuments.set([]);
      }

      this.load(sel);
    });
  }

  // ── Title (rename) handlers ──────────────────────────────────────────

  protected onStartRename(): void {
    if (!this.canRename()) return;
    const sel = this.selection();
    if (!sel) return;
    const currentName =
      sel.kind === 'folder'
        ? (this.folder()?.displayName ?? this.folder()?.name ?? '')
        : (this.document()?.name ?? '');
    this.titleDraft.set(currentName);
    this.titleError.set(null);
    this.titleEditing.set(true);
    // Focus + select-all on the next microtask so the input is in the DOM.
    queueMicrotask(() => {
      const inputEl = this.titleInput()?.nativeElement;
      if (!inputEl) return;
      inputEl.focus();
      inputEl.select();
    });
  }

  protected onCancelRename(): void {
    this.titleEditing.set(false);
    this.titleError.set(null);
  }

  protected onTitleInput(event: Event): void {
    this.titleDraft.set((event.target as HTMLInputElement).value);
    if (this.titleError()) this.titleError.set(null);
  }

  protected onTitleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      void this.onSaveRename();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.onCancelRename();
    }
  }

  /**
   * PATCH the renamed entity. We update the local snapshot from the response and emit `renamed`
   * so the browser refreshes the column where the row lives. On 409 (`name_taken`) or invalid
   * input the backend's translated message lands inline below the input — same shape as the
   * description editor's error path.
   */
  protected async onSaveRename(): Promise<void> {
    const sel = this.selection();
    if (!sel || !this.canRename()) return;
    const nameValidation = validateFolderOrDocumentName(this.titleDraft());
    if (!nameValidation.valid) {
      this.titleError.set(
        this.translate.instant(folderOrDocumentNameErrorKey(nameValidation.reason))
      );
      return;
    }
    const newName = nameValidation.value;
    const currentName =
      sel.kind === 'folder'
        ? (this.folder()?.displayName ?? this.folder()?.name ?? '')
        : (this.document()?.name ?? '');
    if (newName === currentName) {
      this.onCancelRename();
      return;
    }
    this.titleSaving.set(true);
    this.titleError.set(null);
    try {
      if (sel.kind === 'folder') {
        const folder = this.folder();
        if (!folder?.id) return;
        const updated = await this.foldersService.update(folder.id, { name: newName });
        if (this.selection()?.id === folder.id) {
          this.folder.set({
            ...folder,
            name: updated.name ?? newName,
            displayName: updated.displayName,
          });
          this.titleEditing.set(false);
          this.renamed.emit({
            kind: 'folder',
            id: folder.id,
            parentId: folder.parentId ?? null,
            name: updated.name ?? newName,
          });
        }
      } else {
        const doc = this.document();
        if (!doc?.id) return;
        const updated = await this.documentsService.patch(doc.id, { name: newName });
        if (this.selection()?.id === doc.id) {
          this.document.set({ ...doc, name: updated.name ?? newName });
          this.titleEditing.set(false);
          this.renamed.emit({
            kind: 'document',
            id: doc.id,
            parentId: doc.folderId ?? null,
            name: updated.name ?? newName,
          });
        }
      }
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.RENAME_FAILED'
      );
      this.titleError.set(message);
    } finally {
      this.titleSaving.set(false);
    }
  }

  // ── Tag handlers ─────────────────────────────────────────────────────

  /**
   * Apply the new tag set emitted by the editor. We update the local snapshot optimistically (so
   * the chips re-render immediately), PATCH `tag_ids` with the desired final set, and roll back
   * if the request fails. The folder/document response that comes back is the canonical post-PATCH
   * state — including any tag rows the server resolved on its end — so we replace the local
   * snapshot with the response's tags rather than trusting the optimistic copy.
   */
  /**
   * Refresh available tags whenever the user opens the editor's "+ Add tag" affordance. The
   * cache is otherwise loaded once per session — this triggers a fresh fetch at the one moment
   * staleness matters (another user may have created a tag from a different tab).
   */
  protected onTagSearchOpened(): void {
    void this.tagsService.refresh();
  }

  protected onTagsChange(newTags: TagEditorItem[]): void {
    const sel = this.selection();
    if (!sel) return;
    if (sel.kind === 'folder') void this.replaceFolderTags(newTags);
    else void this.replaceDocumentTags(newTags);
  }

  private async replaceFolderTags(newTags: TagEditorItem[]): Promise<void> {
    const folder = this.folder();
    if (!folder?.id) return;
    const previousTags = folder.tags;
    this.folder.set({ ...folder, tags: toTagModels(newTags) });
    try {
      const updated = await this.foldersService.update(folder.id, {
        tagIds: new Set(newTags.map((t) => t.id)),
      });
      if (this.selection()?.id === folder.id) {
        this.folder.set({ ...folder, tags: updated.tags });
      }
      this.tagsUpdated.emit({ kind: 'folder', id: folder.id, parentId: folder.parentId ?? null });
    } catch {
      if (this.selection()?.id === folder.id) {
        this.folder.set({ ...folder, tags: previousTags });
      }
    }
  }

  private async replaceDocumentTags(newTags: TagEditorItem[]): Promise<void> {
    const doc = this.document();
    if (!doc?.id) return;
    const previousTags = doc.tags;
    this.document.set({ ...doc, tags: toTagModels(newTags) });
    try {
      const updated = await this.documentsService.patch(doc.id, {
        tagIds: new Set(newTags.map((t) => t.id)),
      });
      if (this.selection()?.id === doc.id) {
        this.document.set({ ...doc, tags: updated.tags });
      }
      this.tagsUpdated.emit({ kind: 'document', id: doc.id, parentId: doc.folderId ?? null });
    } catch {
      if (this.selection()?.id === doc.id) {
        this.document.set({ ...doc, tags: previousTags });
      }
    }
  }

  /**
   * Create a tag from the editor's "Create '<query>'" affordance and append it to the current
   * selection. Picks a default color from a small palette so the user gets a visible chip
   * immediately; later they can edit the color via the global tags admin (out of scope here).
   */
  protected async onTagCreate(name: string): Promise<void> {
    const sel = this.selection();
    if (!sel) return;
    try {
      const created = await this.tagsService.createTag(name, defaultTagColor());
      // Server always populates id/name/color on create — codegen types them as optional, so
      // bail out instead of forwarding undefineds into the editor.
      if (!created.id || !created.name || !created.color) return;
      const currentTags: TagEditorItem[] =
        sel.kind === 'folder'
          ? toTagModels(this.folder()?.tags ?? [])
          : toTagModels(this.document()?.tags ?? []);
      this.onTagsChange([
        ...currentTags,
        { id: created.id, name: created.name, color: created.color },
      ]);
    } catch {
      // Caller-side toast / inline error would go here; for now silent — the editor stays open
      // and the user can retry. Worth surfacing once we have a unified error pattern.
    }
  }

  // ── Soft-delete / hard-delete (folder) / restore handlers ───────────
  // Soft-delete is documents-only and gated on `documents.delete` (same grant as hard-delete).
  // Restore is documents-only and gated on `documents.update`. Folders are never soft-deleted in
  // the new model: their lifecycle action is "Delete" (cascade hard-delete; documents inside
  // are soft-deleted with their `original_path` captured).

  /**
   * Soft-delete the currently-selected document — moves it to the user's Trash. Folder selections
   * take the {@link onDeleteFolder} path instead. Confirms via PrimeNG; on success we patch the
   * local snapshot so the header flips to "DELETED" + Restore without a round-trip and emit
   * `softDeleted` so the parent column refreshes its row list.
   */
  protected async onSoftDelete(): Promise<void> {
    const sel = this.selection();
    if (!sel || sel.kind !== 'document' || !this.canSoftDelete() || this.lifecycleSaving()) return;
    const targetName = this.document()?.name ?? '';
    this.confirmationService.confirm({
      header: this.translate.instant('DOCUMENT_MANAGEMENT.DELETE_DOCUMENT_CONFIRM_TITLE'),
      message: this.translate.instant('DOCUMENT_MANAGEMENT.DELETE_DOCUMENT_CONFIRM_MESSAGE', {
        name: targetName,
      }),
      acceptLabel: this.translate.instant('DOCUMENT_MANAGEMENT.DELETE'),
      rejectLabel: this.translate.instant('GENERAL.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => void this.performSoftDelete(sel),
    });
  }

  /**
   * Hard-delete the currently-selected folder. The backend cascades the deletion to the entire
   * subtree and soft-deletes every contained document (capturing each document's `original_path`).
   * Strong confirmation since the folder structure itself is gone afterwards (only files survive,
   * recoverable individually from the Trash).
   */
  protected async onDeleteFolder(): Promise<void> {
    const sel = this.selection();
    if (!sel || sel.kind !== 'folder' || !this.canDeleteFolder() || this.lifecycleSaving()) return;
    const folder = this.folder();
    if (!folder?.id) return;
    const folderId = folder.id;
    const parentId = folder.parentId ?? null;
    const targetName = folder.displayName ?? folder.name ?? '';
    this.confirmationService.confirm({
      header: this.translate.instant('DOCUMENT_MANAGEMENT.DELETE_FOLDER_CONFIRM_TITLE'),
      message: this.translate.instant('DOCUMENT_MANAGEMENT.DELETE_FOLDER_CONFIRM_MESSAGE', {
        name: targetName,
      }),
      acceptLabel: this.translate.instant('DOCUMENT_MANAGEMENT.DELETE'),
      rejectLabel: this.translate.instant('GENERAL.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        void this.performDeleteFolder(sel, folderId, parentId);
      },
    });
  }

  private async performDeleteFolder(
    sel: DetailSelection,
    folderId: string,
    parentId: string | null
  ): Promise<void> {
    this.lifecycleSaving.set(true);
    this.lifecycleError.set(null);
    try {
      await this.foldersService.delete(folderId);
      // Re-use the `softDeleted` event for the lifecycle handler; the parent's handler reads
      // `event.kind` and runs the folder-specific column-truncate-and-jump-to-parent path.
      this.softDeleted.emit({ kind: 'folder', id: folderId, parentId });
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.DELETE_FOLDER_FAILED'
      );
      this.lifecycleError.set(message);
    } finally {
      this.lifecycleSaving.set(false);
    }
    // Suppress "unused parameter" — kept so the signature mirrors performSoftDelete for parity.
    void sel;
  }

  /**
   * Permanently remove a soft-deleted document. The backend requires the row to already be
   * soft-deleted (`document-management/must-be-soft-deleted-first`) and the action is
   * irreversible — confirm with stronger wording than the soft-delete prompt.
   */
  protected onHardDelete(): void {
    const sel = this.selection();
    if (!sel || sel.kind !== 'document' || !this.canHardDelete() || this.lifecycleSaving()) return;
    const targetName = this.document()?.name ?? '';
    this.confirmationService.confirm({
      header: this.translate.instant('DOCUMENT_MANAGEMENT.HARD_DELETE_CONFIRM_TITLE'),
      message: this.translate.instant('DOCUMENT_MANAGEMENT.HARD_DELETE_CONFIRM_MESSAGE', {
        name: targetName,
      }),
      acceptLabel: this.translate.instant('DOCUMENT_MANAGEMENT.DELETE_PERMANENTLY'),
      rejectLabel: this.translate.instant('GENERAL.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        void this.performHardDelete(sel);
      },
    });
  }

  private async performHardDelete(sel: DetailSelection): Promise<void> {
    if (sel.kind !== 'document') return;
    const doc = this.document();
    if (!doc?.id) return;
    const documentId = doc.id;
    this.lifecycleSaving.set(true);
    this.lifecycleError.set(null);
    try {
      await this.documentsService.delete(documentId);
      // The row is gone; the parent will clear its selection and refresh the Trash column. We
      // don't touch the local snapshot — the effect that reacts to `selection` change will do
      // the cleanup once the parent emits a null selection.
      this.hardDeleted.emit({
        kind: 'document',
        id: documentId,
        parentId: doc.folderId ?? null,
      });
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.HARD_DELETE_FAILED'
      );
      this.lifecycleError.set(message);
    } finally {
      this.lifecycleSaving.set(false);
    }
  }

  private async performSoftDelete(sel: DetailSelection): Promise<void> {
    this.lifecycleSaving.set(true);
    this.lifecycleError.set(null);
    try {
      const doc = this.document();
      if (!doc?.id || sel.kind !== 'document') return;
      const updated = await this.documentsService.softDelete(doc.id);
      if (this.selection()?.id === doc.id) {
        this.document.set({
          ...doc,
          softDeletedAt: updated.softDeletedAt,
          softDeletedBy: updated.softDeletedBy,
          folderId: updated.folderId,
          originalPath: updated.originalPath,
        });
      }
      this.softDeleted.emit({ kind: 'document', id: doc.id, parentId: doc.folderId ?? null });
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.DELETE_FAILED'
      );
      this.lifecycleError.set(message);
    } finally {
      this.lifecycleSaving.set(false);
    }
  }

  /**
   * Restore the current selection from soft delete. On `document-management/name-taken` the backend says a live
   * sibling reclaimed the name while the document was deleted — instead of falling through to a generic
   * inline error, open the rename-on-restore dialog so the user can pick a new name and retry in
   * one click.
   */
  protected async onRestore(): Promise<void> {
    const sel = this.selection();
    if (!sel || !this.canRestore() || this.lifecycleSaving()) return;
    await this.performRestore(sel, undefined);
  }

  /** User typed a new name in the rename-on-restore dialog and clicked Save. */
  protected async onRestoreRenameSave(): Promise<void> {
    const sel = this.selection();
    if (!sel) return;
    const nameValidation = validateFolderOrDocumentName(this.restoreRenameDraft());
    if (!nameValidation.valid) {
      this.restoreRenameError.set(
        this.translate.instant(folderOrDocumentNameErrorKey(nameValidation.reason))
      );
      return;
    }
    this.restoreRenameError.set(null);
    await this.performRestore(sel, nameValidation.value);
  }

  protected onRestoreRenameCancel(): void {
    this.restoreRenameDialogVisible.set(false);
    this.restoreRenameDraft.set('');
    this.restoreRenameError.set(null);
  }

  protected onRestoreRenameInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.restoreRenameDraft.set(target.value);
    if (this.restoreRenameError()) this.restoreRenameError.set(null);
  }

  /** Used by both the plain Restore button and the rename-on-restore dialog Save button. */
  private async performRestore(sel: DetailSelection, newName: string | undefined): Promise<void> {
    if (sel.kind !== 'document') return; // Folders are never soft-deleted in the new model.
    this.lifecycleSaving.set(true);
    this.lifecycleError.set(null);
    const isRename = newName !== undefined;
    try {
      const doc = this.document();
      if (!doc?.id) return;
      const updated = await this.documentsService.restore(
        doc.id,
        isRename ? { conflictResolution: 'rename', newName } : {}
      );
      if (this.selection()?.id === doc.id) {
        this.document.set({
          ...doc,
          name: updated.name ?? doc.name,
          folderId: updated.folderId,
          softDeletedAt: updated.softDeletedAt,
          softDeletedBy: updated.softDeletedBy,
          originalPath: updated.originalPath,
        });
      }
      this.restored.emit({ kind: 'document', id: doc.id, parentId: updated.folderId ?? null });
      // Close the rename dialog on success (no-op if it wasn't open).
      this.restoreRenameDialogVisible.set(false);
      this.restoreRenameDraft.set('');
    } catch (err) {
      const problem = await parseProblemFromError(err);
      const isNameTaken = problem?.type?.endsWith('document-management/name-taken') === true;
      if (isNameTaken && !isRename) {
        // First attempt: surface the rename dialog instead of an inline error so the user can
        // recover in one click.
        const currentName = this.document()?.name ?? '';
        this.restoreRenameDraft.set(currentName);
        this.restoreRenameError.set(null);
        this.restoreRenameDialogVisible.set(true);
        return;
      }
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.RESTORE_FAILED'
      );
      // While the rename dialog is open, render errors inside it; otherwise fall back to the
      // header banner.
      if (this.restoreRenameDialogVisible()) {
        this.restoreRenameError.set(message);
      } else {
        this.lifecycleError.set(message);
      }
    } finally {
      this.lifecycleSaving.set(false);
    }
  }

  // ── Description handlers ─────────────────────────────────────────────

  protected onStartEditDescription(): void {
    if (!this.canEditDescription()) return;
    this.descriptionDraft.set(this.document()?.description ?? '');
    this.descriptionError.set(null);
    this.descriptionEditing.set(true);
  }

  protected onCancelEditDescription(): void {
    this.descriptionEditing.set(false);
    this.descriptionError.set(null);
  }

  protected onDescriptionInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.descriptionDraft.set(target.value);
  }

  /**
   * PATCH the document's description. The PATCH response is the new persisted state, so we
   * replace the local snapshot with it. Empty/whitespace input sends `description: null` so the
   * backend's JSON-Merge-Patch handler clears the field; non-empty input sends the trimmed text.
   */
  protected async onSaveDescription(): Promise<void> {
    const doc = this.document();
    if (!doc?.id) return;
    const draft = this.descriptionDraft().trim();
    this.descriptionSaving.set(true);
    this.descriptionError.set(null);
    try {
      const updated = await this.documentsService.patch(doc.id, {
        description: draft.length === 0 ? null : draft,
      });
      if (this.selection()?.id === doc.id) {
        // PATCH returns the lighter `DocumentResponse`; merge into the existing detail snapshot
        // so we don't drop fields like `starred` or `description` that aren't on the wire format.
        this.document.set({ ...doc, description: updated.description ?? undefined });
        this.descriptionEditing.set(false);
      }
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.DESCRIPTION_SAVE_FAILED'
      );
      this.descriptionError.set(message);
    } finally {
      this.descriptionSaving.set(false);
    }
  }

  /**
   * Kick off a fresh extraction on the document's current version. The server returns a
   * `pending` job; we point the visible extraction at it and spin up the poll loop.
   */
  protected async onAnalyze(): Promise<void> {
    if (!this.canAnalyze()) return;
    const doc = this.document();
    if (!doc?.id || !doc.currentVersionId) return;
    const documentId = doc.id;
    const versionId = doc.currentVersionId;
    this.extractionStarting.set(true);
    this.extractionError.set(null);
    try {
      const start = await this.documentsService.analyze(documentId, versionId);
      if (!start.extractionId) throw new Error('Server did not return an extraction id');
      this.extraction.set({ id: start.extractionId, status: start.status, documentId, versionId });
      this.pollExtraction(start.extractionId, documentId, versionId);
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.ANALYSIS_FAILED'
      );
      this.extractionError.set(message);
    } finally {
      this.extractionStarting.set(false);
    }
  }

  protected onDismissExtractionError(): void {
    this.extractionError.set(null);
  }

  protected async onToggleHistory(): Promise<void> {
    const next = !this.historyExpanded();
    this.historyExpanded.set(next);
    if (!next) return;
    const doc = this.document();
    if (!doc?.id || !doc.currentVersionId) return;
    try {
      const list = await this.documentsService.listExtractions(doc.id, doc.currentVersionId);
      if (this.selection()?.id !== doc.id) return;
      this.extractionHistory.set(list.items ?? []);
    } catch {
      this.extractionHistory.set([]);
    }
  }

  protected onSelectHistoryEntry(entry: DocumentExtractionResponse): void {
    this.extraction.set(entry);
    if (
      entry.id &&
      entry.documentId &&
      entry.versionId &&
      isRunningExtractionStatus(entry.status)
    ) {
      this.pollExtraction(entry.id, entry.documentId, entry.versionId);
    }
  }

  protected isExtractionSelected(entry: DocumentExtractionResponse): boolean {
    return entry.id != null && entry.id === this.extraction()?.id;
  }

  protected onToggleAiAnalysisExpanded(): void {
    this.documentManagementPreferences.setAiAnalysisExpanded(!this.isAiAnalysisExpanded());
  }

  /**
   * Backup polling for active extractions. SSE normally drives immediate refreshes; this keeps
   * progress moving if the stream is disconnected or a lifecycle event was missed.
   */
  private pollExtraction(extractionId: string, documentId: string, versionId: string): void {
    if (this.extractionPollCancel) this.extractionPollCancel.cancelled = true;
    const cancelToken = { cancelled: false };
    this.extractionPollCancel = cancelToken;
    void this.runExtractionPoll(extractionId, documentId, versionId, cancelToken, 0);
  }

  private async runExtractionPoll(
    extractionId: string,
    documentId: string,
    versionId: string,
    cancelToken: { cancelled: boolean },
    attempt: number
  ): Promise<void> {
    if (cancelToken.cancelled || attempt > 180) return;
    const terminal = await this.refreshExtractionFromServer(
      extractionId,
      documentId,
      versionId,
      cancelToken
    );
    if (cancelToken.cancelled) return;
    if (this.selection()?.id !== documentId) return;
    if (terminal) {
      await this.refreshExtractionHistoryIfOpen(documentId, versionId, cancelToken);
      return;
    }
    globalThis.setTimeout(
      () =>
        void this.runExtractionPoll(extractionId, documentId, versionId, cancelToken, attempt + 1),
      this.extractionPollDelayMs()
    );
  }

  private extractionPollDelayMs(): number {
    return this.extractionEvents.connectionState() === 'connected' ? 15000 : 2000;
  }

  private async refreshExtractionFromServer(
    extractionId: string,
    documentId: string,
    versionId: string,
    cancelToken?: { cancelled: boolean }
  ): Promise<boolean> {
    let next: DocumentExtractionResponse | null = null;
    try {
      next = await this.documentsService.getExtractionById(extractionId);
    } catch {
      return false;
    }
    if (cancelToken?.cancelled) return false;
    const sel = this.selection();
    if (sel?.kind !== 'document' || sel.id !== documentId) return false;
    const currentVersionId = this.document()?.currentVersionId;
    if (currentVersionId && currentVersionId !== versionId) return false;

    this.extraction.set(next);
    return !isRunningExtractionStatus(next.status);
  }

  private async onExtractionEvent(event: DocumentExtractionEvent): Promise<void> {
    if (!event.documentId || !event.versionId || !event.extractionId) return;
    const sel = this.selection();
    if (sel?.kind !== 'document' || sel.id !== event.documentId) return;
    const currentVersionId = this.document()?.currentVersionId;
    if (currentVersionId && currentVersionId !== event.versionId) return;

    const terminal = await this.refreshExtractionFromServer(
      event.extractionId,
      event.documentId,
      event.versionId
    );
    if (isRunningExtractionStatus(event.status)) {
      this.pollExtraction(event.extractionId, event.documentId, event.versionId);
      return;
    }
    if (terminal && this.extractionPollCancel) {
      this.extractionPollCancel.cancelled = true;
      this.extractionPollCancel = null;
    }
    await this.refreshExtractionHistoryIfOpen(event.documentId, event.versionId);
  }

  private async refreshExtractionHistoryIfOpen(
    documentId: string,
    versionId: string,
    cancelToken?: { cancelled: boolean }
  ): Promise<void> {
    if (!this.historyExpanded()) return;
    try {
      const list = await this.documentsService.listExtractions(documentId, versionId);
      if (!cancelToken?.cancelled) this.extractionHistory.set(list.items ?? []);
    } catch {
      // Leave the existing history list in place.
    }
  }

  protected onClose(): void {
    this.closeRequested.emit();
  }

  protected isHeaderStarred(): boolean {
    const sel = this.selection();
    return sel != null && this.starredIds().has(sel.id);
  }

  protected onToggleHeaderStar(): void {
    const sel = this.selection();
    if (!sel || !this.canStar()) return;
    this.starToggled.emit(sel);
  }

  protected onRecentClick(doc: DocumentResponse): void {
    this.recentDocumentClicked.emit(doc);
  }

  protected mimeColorClassFor(mime: string | undefined): string {
    return mimeColorClass(mime);
  }

  protected async onDownload(): Promise<void> {
    const doc = this.document();
    if (!doc?.id || !doc.currentVersionId) return;
    this.downloadInFlight.set(true);
    try {
      const presigned = await this.documentsService.download(doc.id, doc.currentVersionId);
      if (presigned.url) window.open(presigned.url, '_blank', 'noopener');
    } finally {
      this.downloadInFlight.set(false);
    }
  }

  protected formatSize(bytes: number | undefined): string {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  protected mimeShortLabel(mime: string | undefined): string {
    if (!mime) return '—';
    const slash = mime.indexOf('/');
    return slash >= 0
      ? mime
          .slice(slash + 1)
          .toUpperCase()
          .slice(0, 4)
      : mime.slice(0, 4).toUpperCase();
  }

  /** Returns true for the version that's currently the document's `current_version_id`. */
  protected isCurrentVersion(version: DocumentVersionResponse): boolean {
    return version.id != null && version.id === this.document()?.currentVersionId;
  }

  /** Returns true while a version's binary upload hasn't been finalized yet. */
  protected isPendingVersion(version: DocumentVersionResponse): boolean {
    return version.finalizedAt == null;
  }

  protected async onDownloadVersion(version: DocumentVersionResponse): Promise<void> {
    const doc = this.document();
    if (!doc?.id || !version.id || this.isPendingVersion(version)) return;
    this.versionDownloadingId.set(version.id);
    try {
      const presigned = await this.documentsService.download(doc.id, version.id);
      if (presigned.url) window.open(presigned.url, '_blank', 'noopener');
    } finally {
      this.versionDownloadingId.set(null);
    }
  }

  protected onUploadNewVersionClick(): void {
    if (!this.canUploadNewVersion()) return;
    this.newVersionInput()?.nativeElement.click();
  }

  protected onNewVersionFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    target.value = ''; // allow re-selecting the same file later
    if (file) void this.uploadNewVersion(file);
  }

  /**
   * Three-step pipeline for replacing the current version with a freshly-uploaded one:
   * `initiateVersionUpload` → multipart POST to the presigned form → `finalizeUpload`.
   * On success the document detail + versions list both reload so the new version is visible
   * and the metadata (size, mime, current version pointer) refreshes. Errors stay inline.
   *
   * Client-side MIME gate runs first so the user gets an immediate, translated error without a
   * round-trip; the server enforces the same rule (`document-management/version-mime-mismatch`).
   */
  private async uploadNewVersion(file: File): Promise<void> {
    const doc = this.document();
    if (!doc?.id) return;
    const documentId = doc.id;
    const newMime = file.type || 'application/octet-stream';
    const expectedMime = doc.mimeType;
    if (expectedMime && expectedMime !== newMime) {
      this.newVersionError.set(
        this.translate.instant('DOCUMENT_MANAGEMENT.PROBLEM.VERSION_MIME_MISMATCH', {
          expected: expectedMime,
          actual: newMime,
        })
      );
      return;
    }
    this.isUploadingNewVersion.set(true);
    this.newVersionError.set(null);
    try {
      const intent = await this.documentsService.initiateVersionUpload({
        documentId,
        mimeType: newMime,
        originalFileName: file.name,
      });
      if (!intent.documentId || !intent.upload?.url || !intent.upload.formFields) {
        throw new Error('Server did not return a complete upload intent');
      }
      await uploadBinary(intent.upload.url, intent.upload.formFields, file);
      await this.documentsService.finalizeUpload(intent.documentId);
      // Bypass the `selection`-tied effect — the id hasn't changed, so we re-fetch ourselves.
      const sel = this.selection();
      if (sel?.kind === 'document' && sel.id === documentId) {
        await this.refreshDocument(documentId);
      }
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.UPLOAD_FAILED'
      );
      this.newVersionError.set(message);
    } finally {
      this.isUploadingNewVersion.set(false);
    }
  }

  protected dismissNewVersionError(): void {
    this.newVersionError.set(null);
  }

  private async refreshDocument(documentId: string): Promise<void> {
    try {
      const [doc, versions] = await Promise.all([
        this.documentsService.getById(documentId),
        this.documentsService.listVersions(documentId),
      ]);
      if (this.selection()?.id !== documentId) return;
      this.document.set(doc);
      this.versions.set(sortVersionsNewestFirst(versions));
      // Drop the previous version's presigned preview URL and refetch for the new current
      // version. The constructor's selection-effect only resets previewUrl on selection change,
      // so without this the tile would keep rendering the stale (and soon-to-404) rendition.
      this.previewUrl.set(null);
      if (doc.currentVersionId && doc.previewAvailable === true) {
        void this.loadPreviewUrl(documentId, doc.currentVersionId);
      }
    } catch {
      // Leave the existing snapshot in place — caller may show a banner via newVersionError.
    }
  }

  private async load(sel: DetailSelection): Promise<void> {
    this.isLoading.set(true);
    try {
      if (sel.kind === 'folder') await this.loadFolder(sel.id);
      else await this.loadDocument(sel.id);
    } catch {
      if (this.selection()?.id === sel.id) this.hasError.set(true);
    } finally {
      if (this.selection()?.id === sel.id) this.isLoading.set(false);
    }
  }

  /**
   * Folder detail + the 5 most recent docs in this folder's subtree are fetched together so
   * the pane finishes loading with both visible — saves a noticeable flicker on slower links
   * and matches what the user expects ("show me what's new in here").
   */
  private async loadFolder(folderId: string): Promise<void> {
    const [folder, recent] = await Promise.all([
      this.foldersService.getById(folderId),
      this.documentsService
        .listRecent({ folderId, limit: 5 })
        .catch(() => [] as DocumentResponse[]),
    ]);
    if (this.selection()?.id !== folderId) return;
    this.folder.set(folder);
    this.folderRecentDocuments.set(recent);
  }

  /**
   * Documents fetch detail + version history first, then hydrate the AI Analyze panel with the
   * newest active extraction if one exists, otherwise the newest succeeded result.
   */
  private async loadDocument(documentId: string): Promise<void> {
    const [doc, versions] = await Promise.all([
      this.documentsService.getById(documentId),
      this.documentsService.listVersions(documentId).catch(() => [] as DocumentVersionResponse[]),
    ]);
    if (this.selection()?.id !== documentId) return;
    this.document.set(doc);
    this.versions.set(sortVersionsNewestFirst(versions));
    if (doc.currentVersionId && doc.previewAvailable === true) {
      void this.loadPreviewUrl(documentId, doc.currentVersionId);
    }
    if (!doc.currentVersionId) return;
    const list = await this.documentsService
      .listExtractions(documentId, doc.currentVersionId)
      .catch(() => null);
    if (this.selection()?.id !== documentId) return;

    const items = list?.items ?? [];
    const visible =
      items.find((item) => isRunningExtractionStatus(item.status)) ??
      items.find((item) => item.status === 'succeeded') ??
      (await this.documentsService.getLatestSucceededExtraction(documentId, doc.currentVersionId));
    if (this.selection()?.id === documentId && visible) {
      this.extraction.set(visible);
      if (visible.id && isRunningExtractionStatus(visible.status)) {
        this.pollExtraction(visible.id, documentId, doc.currentVersionId);
      }
    }
  }

  /**
   * Fetches a presigned URL for the inline JPEG preview rendition. Best-effort: failures are
   * swallowed so the detail pane simply falls back to the no-preview tile. Re-fired only when
   * the selection or current version changes (the parent effect resets `previewUrl` first).
   */
  private async loadPreviewUrl(documentId: string, versionId: string): Promise<void> {
    this.previewLoading.set(true);
    try {
      const presigned = await this.documentsService.download(documentId, versionId, 'preview');
      if (this.selection()?.id !== documentId) return;
      this.previewUrl.set(presigned.url ?? null);
    } catch {
      // Treat any failure as "no inline preview" — the tile keeps the click-to-open affordance
      // for previewable MIMEs and falls back to a simple placeholder otherwise.
      if (this.selection()?.id === documentId) {
        this.previewUrl.set(null);
      }
    } finally {
      if (this.selection()?.id === documentId) {
        this.previewLoading.set(false);
      }
    }
  }

  protected async onOpenPreview(): Promise<void> {
    const doc = this.document();
    if (!doc?.id || !doc.currentVersionId) return;
    const documentId = doc.id;
    const versionId = doc.currentVersionId;
    const documentsService = this.documentsService;
    const data: FilePreviewDialogData = {
      mimeType: doc.mimeType ?? null,
      name: doc.name,
      // Closure over the documents service so the shared dialog stays data-source agnostic. The
      // dialog calls this on open; we don't pre-resolve here so URLs that expire between modal
      // opens get a fresh fetch every time.
      fetchUrl: async () => {
        const presigned = await documentsService.download(documentId, versionId);
        if (!presigned.url) {
          throw new Error('Empty download URL');
        }
        return presigned.url;
      },
    };
    const { FilePreviewDialogComponent } = await import('@tourfold/libraries/file-preview');
    // No `header` — the dialog renders its own internal toolbar with title/download/close so we
    // can guarantee a close affordance even when the underlying PrimeNG header slot disagrees.
    this.dialogService.open(FilePreviewDialogComponent, {
      showHeader: false,
      width: '90vw',
      height: '90vh',
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      data,
      contentStyle: { padding: '0', overflow: 'hidden', height: '100%' },
    });
  }
}

/**
 * Whether the dialog overlay can render this MIME inline. Returns true for PDF, supported
 * raster images, markdown and plain/CSV text. Anything else falls back to a "download only"
 * message in the dialog, so the tile click affordance is suppressed up front.
 */
function isOverlayPreviewable(mimeType: string | null | undefined): boolean {
  if (!mimeType) return false;
  const normalized = mimeType.trim().toLowerCase();
  if (normalized === 'application/pdf') return true;
  if (normalized.startsWith('image/')) return true;
  if (normalized === 'text/markdown' || normalized === 'text/x-markdown') return true;
  if (normalized === 'text/plain' || normalized === 'text/csv') return true;
  return false;
}

/**
 * Returns versions sorted newest-first by `version_number` (highest first). The backend already
 * orders this way, but a local sort keeps the UI deterministic against any backend reordering or
 * caching mishaps.
 */
function sortVersionsNewestFirst(
  versions: readonly DocumentVersionResponse[]
): DocumentVersionResponse[] {
  return [...versions].sort((a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0));
}

/** One key-value row rendered in the AI Analyze section. */
export interface ExtractionFieldRow {
  readonly key: string;
  readonly value: string;
}

/**
 * Flattens an extraction's `extracted_fields` JSON into a list of rows for the detail pane to
 * render. Each top-level key becomes one row; nested objects/arrays are JSON-stringified inline
 * so the user sees the structure without us inventing a tree view this slice. Returns an empty
 * list when the extraction is missing, hasn't succeeded, or has no fields.
 */
function fieldRowsFromExtraction(
  extraction: DocumentExtractionResponse | null
): readonly ExtractionFieldRow[] {
  if (extraction?.status !== 'succeeded') return [];
  const fields = extraction.extractedFields;
  if (fields == null || typeof fields !== 'object' || Array.isArray(fields)) return [];
  const rows: ExtractionFieldRow[] = [];
  for (const [key, raw] of Object.entries(fields as Record<string, unknown>)) {
    rows.push({ key, value: formatExtractedValue(raw) });
  }
  return rows;
}

function formatExtractedValue(raw: unknown): string {
  if (raw == null) return '—';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
  return JSON.stringify(raw);
}

/**
 * Convert the editor's lightweight `TagEditorItem[]` shape to the API's `DocumentManagementTag[]`.
 * Identity by structural typing — fields match exactly.
 */
function toTagModels(items: readonly TagEditorItem[]): TagEditorItem[] {
  return items.map((t) => ({ id: t.id, name: t.name, color: t.color }));
}

/**
 * Default chip color used when the user creates a tag inline. Picked deterministically from a
 * small Tailwind-ish palette so consecutive new tags don't all look identical, without forcing
 * a color-picker UI in the inline-create flow.
 */
const DEFAULT_TAG_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
] as const;
let nextColorIndex = 0;
function defaultTagColor(): string {
  const color = DEFAULT_TAG_COLORS[nextColorIndex % DEFAULT_TAG_COLORS.length];
  nextColorIndex++;
  return color ?? '#3B82F6';
}
